// MT5PlatformShowcase · broker-only animated MT5 highlight section.
// Left mock window is driven by the real /market/quotes feed (or SignalR when
// connected). User can pick which asset the M5 chart focuses on, and the chart
// bars reflect the actual price drift of that asset over the last 26 ticks.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Apple, Monitor, Smartphone, Globe, Cpu, Layers, Zap, ShieldCheck,
  ArrowRight, Activity, BarChart3, ChevronDown, X, Search,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { MT5_DOWNLOADS } from "@/data/site";
import useLivePrices from "@/hooks/useLivePrices";
import api from "@/lib/api";
import SymbolIcon from "@/components/SymbolIcon";

const WATCHLIST = ["EURUSD", "GBPUSD", "XAUUSD", "BTCUSD", "NAS100", "USOIL"];
const CHART_OPTIONS = [
  { sym: "EURUSD", cat: "forex" }, { sym: "GBPUSD", cat: "forex" }, { sym: "USDJPY", cat: "forex" },
  { sym: "USDCHF", cat: "forex" }, { sym: "AUDUSD", cat: "forex" },
  { sym: "XAUUSD", cat: "metals" }, { sym: "XAGUSD", cat: "metals" },
  { sym: "BTCUSD", cat: "crypto" }, { sym: "ETHUSD", cat: "crypto" },
  { sym: "WS30",   cat: "indices" }, { sym: "SP500",  cat: "indices" }, { sym: "NAS100", cat: "indices" },
  { sym: "DAX40",  cat: "indices" }, { sym: "UK100",  cat: "indices" },
  { sym: "USOIL",  cat: "energies" }, { sym: "UKOIL", cat: "energies" },
];
// Quick lookup so the chart header can find the category of the active symbol.
const CHART_CAT = Object.fromEntries(CHART_OPTIONS.map((o) => [o.sym, o.cat]));

function fmt(sym, v) {
  if (!Number.isFinite(v)) return "·";
  if (/JPY$/.test(sym)) return v.toFixed(3);
  if (sym === "BTCUSD" || sym === "ETHUSD") return Math.round(v).toLocaleString();
  if (/^(US30|NAS100|SPX500|GER40|UK100|JPN225|HK50|FRA40|AUS200|EU50|SWI20|NETH25|JPN|HK)/.test(sym)) return Math.round(v).toLocaleString();
  if (sym === "USOIL" || sym === "UKOIL") return v.toFixed(2);
  if (sym.length === 6) return v.toFixed(5);
  return v.toFixed(2);
}

const DOWNLOADS = [
  { id: "win",    label: "Windows", Icon: Monitor,    href: MT5_DOWNLOADS.windows },
  { id: "mac",    label: "macOS",   Icon: Apple,      href: MT5_DOWNLOADS.mac },
  { id: "ios",    label: "iOS",     Icon: Smartphone, href: MT5_DOWNLOADS.ios },
  { id: "android",label: "Android", Icon: Smartphone, href: MT5_DOWNLOADS.android },
  { id: "web",    label: "Web",     Icon: Globe,      href: MT5_DOWNLOADS.webTerminal },
];

const VALUE_PROPS_META = [
  { Icon: Zap },
  { Icon: Layers },
  { Icon: Cpu },
  { Icon: ShieldCheck },
];

// Live mini-chart · pulls real OHLC bars from the upstream price hub via
// GetHistory(symbol, timeframe). Falls back to a smooth synthetic seed while
// the fetch is in-flight so the chart never appears empty.
function useHistory(symbol, timeframe = "1h") {
  const [bars, setBars] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    setLoading(true);

    // Backend `/market/history` calls into the SignalR hub on first hit
    // and caches the result. When the hub is mid-reconnect or warming
    // up the first call can return 0–2 bars · so we POLL every 1.2 s
    // until we have a usable chart (≥ 4 bars). Once we have enough
    // bars we refresh every 30 s to pick up new candles.
    const fetchOnce = () => {
      api.get(`/market/history?symbol=${encodeURIComponent(symbol)}&timeframe=${encodeURIComponent(timeframe)}`)
        .then(({ data }) => {
          if (cancelled) return;
          const newBars = Array.isArray(data?.bars) ? data.bars : [];
          // Only overwrite our state with the new response if it
          // actually has usable data (≥ 4 bars). When the backend
          // cache is mid-reconnect and returns 0–2 bars, we keep the
          // LAST KNOWN GOOD bars so the chart doesn't flicker back
          // to the skeleton.
          setBars((prev) => (newBars.length >= 4 ? newBars : prev));
          setLoading(false);
          const delay = newBars.length >= 4 ? 30000 : 1200;
          timer = setTimeout(fetchOnce, delay);
        })
        .catch(() => {
          if (cancelled) return;
          setLoading(false);
          timer = setTimeout(fetchOnce, 2000);
        });
    };

    fetchOnce();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [symbol, timeframe]);
  return { bars, loading };
}

// Constrained random-walk seed so the chart looks alive even before the
// real history arrives.
function seedCandles(base, count = 16) {
  const out = [];
  let prev = base;
  for (let i = 0; i < count; i++) {
    const drift = Math.sin(i * 0.45) * 0.0014;
    const next = prev * (1 + drift + (Math.random() - 0.5) * 0.0006);
    const h = Math.max(prev, next) * (1 + Math.random() * 0.0008);
    const l = Math.min(prev, next) * (1 - Math.random() * 0.0008);
    out.push({ t: i, o: prev, h, l, c: next });
    prev = next;
  }
  return out;
}

function LiveChart({ symbol, prices, timeframe = "1m" }) {
  const { t } = useTranslation();
  const { bars, loading } = useHistory(symbol, timeframe);
  const liveBid = prices?.[symbol]?.bid;

  // Show up to 28 candles for a denser, more market-realistic look.
  const N = 28;

  // We ONLY render real bars from the backend (which are pulled from
  // the Just4Broker SignalR feed). If the bars haven't arrived yet we
  // show a skeleton · NO synthetic sine-wave fallback. Traders would
  // immediately spot fake data and lose trust.
  const hasRealBars = bars.length >= 4;
  const historical = React.useMemo(() => {
    if (!hasRealBars) return [];
    return bars.slice(-N).map((b) => ({ open: b.o, high: b.h, low: b.l, close: b.c }));
  }, [bars, symbol]);

  // Y-scale derived from real historical OHLC + the current live bid.
  const { min, max, span } = React.useMemo(() => {
    if (!hasRealBars) return { min: 0, max: 1, span: 1 };
    const allVals = historical.flatMap((c) => [c.high, c.low]);
    if (Number.isFinite(liveBid)) allVals.push(liveBid);
    const rawMin = Math.min(...allVals);
    const rawMax = Math.max(...allVals);
    const rawSpan = rawMax - rawMin || rawMax * 0.0008 || 1;
    const pad = rawSpan * 0.18;
    const mn = rawMin - pad;
    const mx = rawMax + pad;
    return { min: mn, max: mx, span: mx - mn };
  }, [historical, hasRealBars]);

  // Merge live tick into the last candle FOR RENDER ONLY. Historical
  // candles 0..N-2 are referentially stable across ticks, so React
  // doesn't re-key or re-animate them.
  const candles = historical.map((c, i) => {
    if (i !== historical.length - 1 || !Number.isFinite(liveBid)) return c;
    return {
      open: c.open,
      high: Math.max(c.high, liveBid),
      low: Math.min(c.low, liveBid),
      close: liveBid,
    };
  });

  const W = 240;
  const H = 110;
  const padL = 4;
  // Larger right padding so the last live candle isn't kissing the
  // border and we have room for the horizontal price line + label.
  const padR = 22;

  // Loading skeleton · shown only while the real-history fetch is in
  // flight (or returned an empty array). No synthetic seed wave, but
  // we DO render a row of placeholder ghost candles so the card never
  // looks "empty/warped". Once real bars land we swap to the live
  // render below.
  if (!hasRealBars) {
    const ghostN = 24;
    return (
      <div className="relative flex-1 flex flex-col min-h-[120px]" data-testid="mt5-chart-loading">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block w-full h-full min-h-[120px]" aria-hidden>
          <defs>
            <pattern id={`mt5-grid-${symbol}-skel`} width={W / 5} height={H / 4} patternUnits="userSpaceOnUse">
              <path d={`M ${W / 5} 0 L 0 0 0 ${H / 4}`} fill="none" stroke="color-mix(in srgb, var(--brand) 8%, transparent)" strokeWidth="0.3" />
            </pattern>
          </defs>
          <rect width={W} height={H} fill={`url(#mt5-grid-${symbol}-skel)`} />
          {/* Ghost candle row · placeholder bars that pulse · keeps the
              card from looking empty without faking price action. */}
          {Array.from({ length: ghostN }, (_, i) => {
            const slotW = (W - padL - padR) / ghostN;
            const cx = padL + slotW * (i + 0.5);
            // Deterministic height pattern so candles don't jiggle on
            // re-render · pure visual texture, NOT price data.
            const h1 = 18 + ((i * 7) % 22);
            const h2 = 10 + ((i * 5) % 14);
            const yT = (H - h1) / 2 - h2 / 2;
            return (
              <motion.g key={i}
                animate={{ opacity: [0.18, 0.36, 0.18] }}
                transition={{ duration: 1.8, delay: (i % 6) * 0.12, repeat: Infinity, ease: "easeInOut" }}
              >
                <line x1={cx} x2={cx} y1={yT - 4} y2={yT + h1 + 4} stroke="color-mix(in srgb, var(--text-3) 60%, transparent)" strokeWidth="0.5" />
                <rect x={cx - 1.4} y={yT} width="2.8" height={h1} rx="0.5" fill="color-mix(in srgb, var(--text-3) 45%, transparent)" />
              </motion.g>
            );
          })}
          <text x={W / 2} y={H / 2 + 30} textAnchor="middle" fontSize="5" fontFamily="JetBrains Mono, monospace" fill="color-mix(in srgb, var(--brand) 75%, transparent)" letterSpacing="2">
            {loading ? `▾ ${t("ui.mt5Mock.connectingFeed")} ▾` : `▾ ${t("ui.mt5Mock.reconnecting")} ▾`}
          </text>
        </svg>
      </div>
    );
  }

  const innerW = W - padL - padR;
  const slot = innerW / candles.length;
  // Wider body / smaller gap so candles sit closer together · denser
  // market-realistic look.
  const bodyW = Math.max(2.2, slot * 0.78);

  const yOf = (v) => H - ((v - min) / span) * H;
  const lastCandle = candles[candles.length - 1];
  const overallUp = lastCandle.close >= candles[0].open;
  const stroke = overallUp ? "#54d97c" : "#f06a6a";
  const lastCloseY = yOf(lastCandle.close);
  const lastCandleX = padL + slot * (candles.length - 0.5);
  // Defensive · skip the live-overlay primitives if any computed value
  // ended up non-finite (NaN/undefined). This silences React's "cx /
  // cy / y1 / y2 invalid attribute" warnings that surface when the
  // chart re-renders mid-data-swap.
  const overlayOk = Number.isFinite(lastCloseY) && Number.isFinite(lastCandleX);

  return (
    <div className="relative flex-1 flex flex-col min-h-[120px]" data-testid="mt5-chart">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="block w-full h-full min-h-[120px]"
        aria-hidden
      >
        <defs>
          <pattern id={`mt5-grid-${symbol}`} width={W / 5} height={H / 4} patternUnits="userSpaceOnUse">
            <path d={`M ${W / 5} 0 L 0 0 0 ${H / 4}`} fill="none" stroke="color-mix(in srgb, var(--brand) 12%, transparent)" strokeWidth="0.3" />
          </pattern>
        </defs>
        <rect width={W} height={H} fill={`url(#mt5-grid-${symbol})`} />

        {/* Historical candles · plain SVG (no motion, no key change on
            tick) so they DON'T re-animate when a new live tick arrives.
            Only the last candle is a motion element that updates. */}
        {candles.slice(0, -1).map((c, i) => {
          const cx = padL + slot * (i + 0.5);
          const up = c.close >= c.open;
          const col = up ? "#54d97c" : "#f06a6a";
          const yHigh = yOf(c.high);
          const yLow  = yOf(c.low);
          const yTop  = yOf(Math.max(c.open, c.close));
          const yBot  = yOf(Math.min(c.open, c.close));
          const bodyH = Math.max(1.2, yBot - yTop);
          // Skip primitive emission for non-finite coords · happens on
          // first paint before the chart's domain has settled and would
          // otherwise produce "y1/y2/x: undefined" SVG warnings.
          if (![cx, yHigh, yLow, yTop, yBot, bodyH].every(Number.isFinite)) return null;
          return (
            <g key={`${symbol}-h-${i}`}>
              <line x1={cx} x2={cx} y1={yHigh} y2={yLow} stroke={col} strokeWidth="0.9" strokeOpacity="0.95" />
              <rect
                x={cx - bodyW / 2}
                y={yTop}
                width={bodyW}
                height={bodyH}
                rx={0.6}
                fill={up ? col : "color-mix(in srgb, " + col + " 80%, transparent)"}
                stroke={col}
                strokeWidth="0.6"
              />
            </g>
          );
        })}

        {/* LIVE candle · the only motion element. Its body height /
            wick high-low update smoothly as new ticks arrive. */}
        {(() => {
          const i = candles.length - 1;
          const c = candles[i];
          const cx = padL + slot * (i + 0.5);
          const up = c.close >= c.open;
          const col = up ? "#54d97c" : "#f06a6a";
          const yHigh = yOf(c.high);
          const yLow  = yOf(c.low);
          const yTop  = yOf(Math.max(c.open, c.close));
          const yBot  = yOf(Math.min(c.open, c.close));
          const bodyH = Math.max(1.2, yBot - yTop);
          // Skip the live-candle overlay primitives when any computed
          // value is non-finite — happens on first paint before the
          // chart has its data + dimensions resolved, and would
          // otherwise emit React's `cx/y1/y2: undefined` SVG warnings.
          if (![cx, yHigh, yLow, yTop, yBot, bodyH].every(Number.isFinite)) return null;
          return (
            <g key={`${symbol}-live`}>
              <motion.line
                x1={cx} x2={cx}
                y1={yHigh} y2={yLow}
                animate={{ y1: yHigh, y2: yLow }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                stroke={col} strokeWidth="0.9" strokeOpacity="0.95"
              />
              <motion.rect
                x={cx - bodyW / 2}
                y={yTop}
                width={bodyW}
                height={bodyH}
                rx={0.6}
                fill={up ? col : "color-mix(in srgb, " + col + " 80%, transparent)"}
                stroke={col}
                strokeWidth="0.6"
                animate={{ y: yTop, height: bodyH }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              />
            </g>
          );
        })()}

        {/* Live close indicator · a thin horizontal dashed line spanning
            the full chart width at the current close price. Animates its
            Y position smoothly to the new close on every tick, so the
            user sees the price level glide as the candle paints. */}
        {overlayOk && (
          <>
            <motion.line
              x1={0}
              x2={W}
              y1={lastCloseY}
              y2={lastCloseY}
              stroke={stroke}
              strokeWidth="0.6"
              strokeDasharray="3 2"
              strokeOpacity="0.85"
              animate={{ y1: lastCloseY, y2: lastCloseY }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            />

            {/* Live close price tag on the right edge · sits to the right of
                the last candle (in the new padR gutter) so it doesn't
                overlap any candle body. */}
            <motion.rect
              x={W - padR + 2}
              y={lastCloseY - 4.5}
              width={padR - 3}
              height={9}
              rx={1.5}
              fill={stroke}
              animate={{ y: lastCloseY - 4.5 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            />

            {/* Small solid tick marker on the line itself · just a 1.6 px
                square at the last candle's close so the eye can quickly
                find where the live price is. No pulsing animation. */}
            <motion.circle
              cx={lastCandleX}
              cy={lastCloseY}
              r="1.6"
              fill={stroke}
              animate={{ cy: lastCloseY }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            />
          </>
        )}
      </svg>
    </div>
  );
}

// Asset-picker dropdown · responsive. On ≥sm we anchor the panel below
// the trigger (left-aligned so the contents never clip off the left edge
// of the chart card). On mobile we promote the same UI into a centered
// modal sheet with explicit X close so the dropdown can't block the
// chart with no obvious exit.
function ChartPicker({ value, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  // Anchor position computed on open so the desktop dropdown escapes
  // the MT5 frame's overflow-hidden clipping by using fixed positioning.
  const [anchor, setAnchor] = useState({ left: 0, top: 0 });
  const btnRef = useRef(null);
  const list = useMemo(
    () => CHART_OPTIONS.filter((o) => o.sym.toLowerCase().includes(q.trim().toLowerCase())),
    [q]
  );

  // Lock body scroll while the mobile sheet is open so the page doesn't
  // jump around behind the modal.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    if (window.matchMedia("(max-width: 639px)").matches) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Close the picker as soon as the user scrolls the page — otherwise
  // the fixed-position popover stays anchored to viewport coordinates
  // taken at open-time, so it APPEARS to "stick" to the page while the
  // user scrolls (the page moves, the popover doesn't). Desktop only;
  // mobile is a centred sheet that's fine to leave open.
  useEffect(() => {
    if (!open) return;
    const isDesktop = () => window.matchMedia("(min-width: 640px)").matches;
    const onScroll = () => {
      if (isDesktop()) close();
    };
    // Desktop-only: on mobile the virtual keyboard fires a viewport
    // resize the instant the search input focuses, which used to close
    // the picker immediately (looked like the button "did nothing").
    const onResize = () => {
      if (isDesktop()) close();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  // Compute the anchor every time the user opens the picker. Using
  // fixed positioning means the dropdown is no longer subject to its
  // parent's overflow:hidden (which was clipping it inside the MT5
  // terminal mock-up frame).
  const openPicker = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setAnchor({ left: r.left, top: r.bottom + 6 });
    }
    setOpen(true);
    setQ("");
  };

  const close = () => { setOpen(false); setQ(""); };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => (open ? close() : openPicker())}
        className="inline-flex items-center gap-2 h-8 px-2 -mx-2 rounded-lg text-text-1 font-mono text-[12px] uppercase tracking-[0.18em] hover:bg-[color:var(--surface-2)] hover:text-[color:var(--brand)] transition-colors"
        data-testid="mt5-chart-picker"
        aria-expanded={open}
      >
        <SymbolIcon symbol={value} category={CHART_CAT[value]} size={18} />
        {value} · M1 <ChevronDown size={10} strokeWidth={2} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          {/* Backdrop · full-screen dim on mobile, transparent on desktop
              so the dropdown reads as an anchored popover. Tapping
              outside dismisses. */}
          <div
            className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-[2px] sm:bg-transparent sm:backdrop-blur-0"
            onClick={close}
            aria-hidden
          />

          {/* Panel · centred sheet on mobile, FIXED-position popover on
              desktop anchored to the trigger button's measured rect.
              Using `fixed` instead of `absolute` lets the panel render
              OUTSIDE the MT5 frame, which used to clip it. */}
          <div
            className="
              fixed left-1/2 -translate-x-1/2 top-[18%] w-[min(20rem,calc(100vw-2rem))] z-[70]
              sm:left-[var(--popover-l)] sm:top-[var(--popover-t)] sm:translate-x-0 sm:w-[240px] sm:min-w-[240px]
              rounded-2xl border shadow-2xl overflow-hidden
            "
            style={{
              borderColor: "var(--line-strong)",
              background: "var(--surface)",
              "--popover-l": `${anchor.left}px`,
              "--popover-t": `${anchor.top}px`,
            }}
            data-testid="mt5-chart-picker-list"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header strip · search input + explicit close button. */}
            <div className="flex items-center gap-1.5 px-2.5 py-2 border-b" style={{ borderColor: "var(--line)" }}>
              <Search size={12} className="text-text-3 shrink-0" />
              <input
                autoFocus={typeof window !== "undefined" && window.matchMedia("(min-width: 640px)").matches}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("ui.mt5Mock.searchPlaceholder")}
                className="flex-1 min-w-0 h-7 px-1 rounded-md text-[12px] font-mono bg-transparent focus:outline-none"
                style={{ color: "var(--text-1)" }}
                data-testid="mt5-chart-picker-search"
              />
              <button
                type="button"
                onClick={close}
                className="shrink-0 inline-flex items-center justify-center h-7 w-7 rounded-md text-text-3 hover:text-text-1 hover:bg-[color:var(--surface-2)] transition-colors"
                data-testid="mt5-chart-picker-close"
                aria-label={t("ui.mt5Mock.closePicker")}
              >
                <X size={14} />
              </button>
            </div>

            <ul className="max-h-[50vh] sm:max-h-72 overflow-y-auto py-1">
              {list.length === 0 && (
                <li className="px-3 py-3 text-[12px] text-text-3 text-center">{t("ui.mt5Mock.noMatch")} &ldquo;{q}&rdquo;</li>
              )}
              {list.map((o) => (
                <li key={o.sym}>
                  <button
                    type="button"
                    onClick={() => { onChange(o.sym); close(); }}
                    className={`w-full flex items-center gap-2.5 text-left px-3 py-2.5 text-[12px] font-mono tracking-wider transition-colors ${
                      o.sym === value
                        ? "text-[color:var(--brand)] bg-[color:color-mix(in_srgb,var(--brand)_10%,transparent)]"
                        : "text-text-2 hover:text-text-1 hover:bg-[color:var(--surface-2)]"
                    }`}
                    data-testid={`mt5-chart-pick-${o.sym}`}
                  >
                    <SymbolIcon symbol={o.sym} category={o.cat} size={20} />
                    <span className="flex-1">{o.sym}</span>
                    <span className="text-[12px] uppercase tracking-wider text-text-3">{o.cat}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

export default function MT5PlatformShowcase() {
  const { t } = useTranslation();
  const { prices } = useLivePrices();
  const [chartSym, setChartSym] = useState("EURUSD");

  // i18n-driven value props · icons stay static · titles/bodies flow
  // through the active locale via the homepage.mt5.valueProps.* keys.
  const VALUE_PROPS = useMemo(() => VALUE_PROPS_META.map((m, idx) => ({
    ...m,
    title: t(`homepage.mt5.valueProps.v${idx + 1}.title`),
    body:  t(`homepage.mt5.valueProps.v${idx + 1}.body`),
  })), [t]);

  // Live ticking account state · simulated tiny equity / P&L fluctuations
  // so the bottom card feels alive even when the SignalR feed is rate-
  // limited. Realistic ±0.05 % bias per tick around a 12,840.50 baseline.
  const [accountTick, setAccountTick] = useState(0);
  useEffect(() => {
    // 3000ms tick (was 1400ms) · drops re-render frequency by 55% on the
    // homepage without making the equity card feel frozen.
    const id = setInterval(() => setAccountTick((t) => t + 1), 3000);
    return () => clearInterval(id);
  }, []);
  const account = useMemo(() => {
    // Deterministic drift · uses seeded sine so the value walks instead
    // of jittering, looks like a real position breathing.
    const baseBalance = 12840.50;
    const phase = accountTick * 0.42;
    const pnlDrift = Math.sin(phase) * 90 + Math.cos(phase * 0.7) * 30 + 175;
    const equity = baseBalance + pnlDrift;
    const margin = 412.30 + Math.sin(phase * 1.3) * 8;
    return {
      balance: baseBalance,
      equity,
      pl: pnlDrift,
      margin,
    };
  }, [accountTick]);

  // Live watchlist rows · falls back to a placeholder until the first tick arrives.
  const rows = useMemo(
    () => WATCHLIST.map((sym) => ({
      sym,
      val: prices?.[sym]?.bid,
      chg: prices?.[sym]?.change,
      dir: prices?.[sym]?.dir,
      cat: prices?.[sym]?.category,
    })),
    [prices]
  );

  return (
    <section data-testid="mt5-showcase" className="relative py-24 lg:py-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-14 lg:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="pill-mono inline-flex mb-5"
          >
            <BarChart3 size={12} strokeWidth={2.2} className="mr-1.5 -ml-1" style={{ color: "var(--brand)" }} />
            {t("homepage.mt5.section.pill", "The Trading Floor")}
          </motion.div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-[-0.025em] leading-[1.05] text-text-1">
            {t("homepage.mt5.section.head1", "One platform, every market")} · <span className="text-gradient-brand font-semibold">{t("homepage.mt5.section.head2", "MetaTrader 5")}</span>
          </h2>
          <p className="text-text-3 mt-5 max-w-2xl mx-auto text-[15px] leading-relaxed">
            {t("homepage.mt5.section.sub", "The industry standard. Trade 1200+ instruments from a single login · desktop, web or mobile · with institutional execution and full MQL5 support.")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* LEFT · animated MT5 mock with REAL data */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15% 0px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full min-w-0"
          >
            <div
              aria-hidden
              className="absolute inset-0 -z-10"
              style={{ background: "radial-gradient(60% 60% at 50% 50%, color-mix(in srgb, var(--brand) 14%, transparent), transparent 70%)", filter: "blur(40px)" }}
            />
            <div
              className="relative rounded-2xl border overflow-hidden"
              style={{
                borderColor: "color-mix(in srgb, var(--brand) 22%, var(--line-strong))",
                background: "linear-gradient(180deg, color-mix(in srgb, var(--surface) 95%, transparent), color-mix(in srgb, var(--surface-2) 95%, transparent))",
                boxShadow: "0 30px 80px -30px color-mix(in srgb, var(--brand) 28%, transparent), 0 0 0 1px color-mix(in srgb, var(--brand) 12%, transparent)",
              }}
              data-testid="mt5-terminal-mock"
            >
              {/* Title bar · responsive · the long broker tag truncates on
                  mobile so it doesn't wrap on to three lines */}
              <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 border-b min-w-0" style={{ borderColor: "var(--line)", background: "color-mix(in srgb, var(--surface-3) 80%, transparent)" }}>
                <div className="flex gap-1.5 shrink-0">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#ff5f57" }} />
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#febc2e" }} />
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#28c840" }} />
                </div>
                <div className="ml-1 sm:ml-2 flex items-center gap-2 text-text-2 text-[12px] sm:text-[12px] font-mono min-w-0 flex-1">
                  <BarChart3 size={13} strokeWidth={2} style={{ color: "var(--brand)" }} className="shrink-0" />
                  <span className="truncate">{t("ui.mt5Mock.terminalTitle")}</span>
                </div>
                <span className="inline-flex items-center gap-1.5 text-[9.5px] sm:text-[12px] font-mono text-text-3 shrink-0">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: "#54d97c" }} />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: "#54d97c" }} />
                  </span>
                  <span className="hidden xs:inline">{t("ui.mt5Mock.connected")}</span>
                  <span className="xs:hidden">{t("common.live")}</span>
                </span>
              </div>

              {/* Layout · mobile stacks chart above watchlist; desktop is split 7/5.
                  Chart column uses flex-col so the LiveChart can grow into any
                  empty space when the watchlist sibling column is taller (which
                  was causing the chart to sit at the top with a big gap at the
                  bottom). */}
              <div className="grid grid-cols-12 gap-0">
                {/* Chart panel · driven by real live prices · always first on mobile */}
                <div className="order-1 lg:order-2 col-span-12 lg:col-span-5 border-b lg:border-b-0 lg:border-l flex flex-col" style={{ borderColor: "var(--line)" }}>
                  <div className="px-3 sm:px-4 py-2 border-b flex items-center justify-between min-w-0" style={{ borderColor: "var(--line)" }}>
                    <ChartPicker value={chartSym} onChange={setChartSym} />
                    <Activity size={10} style={{ color: "var(--brand)" }} className="shrink-0" />
                  </div>
                  {/* Live price strip · BID + ASK */}
                  {(() => {
                    const p = prices?.[chartSym];
                    const up = (p?.change ?? 0) >= 0;
                    return (
                      <div className="px-3 sm:px-4 pt-3 pb-1">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <motion.span
                            key={`${chartSym}-${(p?.bid ?? 0).toFixed(5)}`}
                            initial={{ opacity: 0.5, y: -2 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="font-mono tabular-nums text-text-1 text-[17px] sm:text-[18px] leading-none"
                          >
                            {fmt(chartSym, p?.bid)}
                          </motion.span>
                          <span
                            className="text-[12px] font-mono tabular-nums px-1.5 py-0.5 rounded-full whitespace-nowrap"
                            style={{
                              background: up ? "rgba(80,220,130,0.12)" : "rgba(240,106,106,0.12)",
                              color: up ? "#54d97c" : "#f06a6a",
                            }}
                          >
                            {up ? "+" : ""}{(p?.change ?? 0).toFixed(2)}%
                          </span>
                        </div>
                        <div className="flex gap-4 mt-1 text-[12px] font-mono tabular-nums text-text-3 uppercase tracking-[0.14em]">
                          <span>{t("common.bid")} <span className="text-text-2 normal-case tracking-normal">{fmt(chartSym, p?.bid)}</span></span>
                          <span>{t("common.ask")} <span className="text-text-2 normal-case tracking-normal">{fmt(chartSym, p?.ask)}</span></span>
                        </div>
                      </div>
                    );
                  })()}
                  <div className="flex-1 min-h-[160px] flex flex-col" data-testid="mt5-chart-wrap">
                    <LiveChart symbol={chartSym} prices={prices} />
                  </div>
                  {/* Account balance card · ticks live so the floor never feels frozen */}
                  <div className="border-t px-3 sm:px-4 py-3 space-y-1.5" style={{ borderColor: "var(--line)", background: "color-mix(in srgb, var(--brand) 4%, transparent)" }}>
                    <div className="flex items-center justify-between gap-3 text-[12px] font-mono uppercase tracking-[0.14em]">
                      <span className="text-text-3">{t("common.balance")}</span>
                      <span className="text-text-1 tabular-nums normal-case tracking-normal">${account.balance.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3 text-[12px] font-mono uppercase tracking-[0.14em]">
                      <span className="text-text-3">{t("common.equity")}</span>
                      <motion.span
                        key={`eq-${accountTick}`}
                        initial={{ opacity: 0.55 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.35 }}
                        className="text-text-1 tabular-nums normal-case tracking-normal"
                      >
                        ${account.equity.toFixed(2)}
                      </motion.span>
                    </div>
                    <div className="flex items-center justify-between gap-3 text-[12px] font-mono uppercase tracking-[0.14em]">
                      <span className="text-text-3">{t("common.profitLoss")}</span>
                      <motion.span
                        key={`pl-${accountTick}`}
                        initial={{ opacity: 0.55, y: -1 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35 }}
                        className="tabular-nums normal-case tracking-normal"
                        style={{ color: account.pl >= 0 ? "#54d97c" : "#f06a6a" }}
                      >
                        {account.pl >= 0 ? "+" : ""}${account.pl.toFixed(2)}
                      </motion.span>
                    </div>
                  </div>
                </div>

                {/* Market watch · second on mobile, left side on desktop */}
                <div className="order-2 lg:order-1 col-span-12 lg:col-span-7">
                  <div className="px-3 sm:px-4 py-2 text-[12px] font-mono uppercase tracking-[0.18em] text-text-3 border-b" style={{ borderColor: "var(--line)" }}>
                    {t("common.marketWatch")}
                  </div>
                  <ul className="divide-y" style={{ borderColor: "var(--line)" }}>
                    {rows.map((r) => {
                      const up = (r.chg ?? 0) >= 0;
                      return (
                        <li
                          key={r.sym}
                          className="grid grid-cols-12 gap-2 px-3 sm:px-4 py-2.5 items-center cursor-pointer hover:bg-[color:var(--surface-2)]"
                          onClick={() => setChartSym(r.sym)}
                          style={{ borderColor: "var(--line)" }}
                          data-testid={`mt5-watch-${r.sym}`}
                        >
                          <span className="col-span-5 sm:col-span-5 flex items-center gap-2 min-w-0">
                            <SymbolIcon symbol={r.sym} category={r.cat} size={18} />
                            <span className="text-text-1 text-[12px] font-medium truncate">{r.sym}</span>
                          </span>
                          <motion.span
                            key={`${r.sym}-${(r.val ?? 0).toFixed(5)}`}
                            initial={{ color: r.dir === "up" ? "#54d97c" : r.dir === "down" ? "#f06a6a" : "var(--text-1)" }}
                            animate={{ color: "var(--text-1)" }}
                            transition={{ duration: 0.5 }}
                            className="col-span-4 text-right text-[12px] font-mono tabular-nums truncate"
                          >
                            {fmt(r.sym, r.val)}
                          </motion.span>
                          <span
                            className="col-span-3 text-right text-[12px] font-mono tabular-nums px-1.5 py-0.5 rounded-full whitespace-nowrap"
                            style={{
                              background: up ? "rgba(80,220,130,0.12)" : "rgba(240,106,106,0.12)",
                              color: up ? "#54d97c" : "#f06a6a",
                            }}
                          >
                            {up ? "+" : ""}{(r.chg ?? 0).toFixed(2)}%
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>

            {/* Platform download row · UNDER the chart card, matching its
                exact width. 5 evenly-spaced tiles inside the chart column. */}
            <div className="grid grid-cols-5 gap-2 mt-4">
              {DOWNLOADS.map((d, i) => (
                <motion.a
                  key={d.id}
                  href={d.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="flex flex-col items-center justify-center gap-1.5 h-16 rounded-xl border text-[12px] font-medium transition-all hover:-translate-y-0.5 hover:border-[color:var(--brand)]"
                  style={{
                    borderColor: "var(--line-strong)",
                    background: "var(--surface)",
                    color: "var(--text-1)",
                    boxShadow: "var(--shadow-sm)",
                  }}
                  data-testid={`mt5-download-${d.id}`}
                >
                  <d.Icon size={20} strokeWidth={1.6} className="text-brand" />
                  <span className="leading-none">{d.label}</span>
                </motion.a>
              ))}
            </div>

          </motion.div>

          {/* RIGHT · value props · centred on mobile (icons stacked above
              the copy), left-aligned alongside the chart on lg+. */}
          <div className="text-center lg:text-left">
            <ul className="space-y-7 lg:space-y-6 max-w-md mx-auto lg:mx-0 lg:max-w-none">
              {VALUE_PROPS.map((vp, i) => (
                <motion.li
                  key={vp.title}
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-10% 0px" }}
                  transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className="flex flex-col items-center text-center gap-2 lg:flex-row lg:items-start lg:text-left lg:gap-4 group"
                >
                  <vp.Icon
                    size={28}
                    strokeWidth={1.6}
                    className="shrink-0 lg:mt-1 text-brand transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3"
                  />
                  <div className="min-w-0">
                    <h3 className="font-display text-lg font-medium text-text-1 mb-1.5 leading-tight">{vp.title}</h3>
                    <p className="text-text-3 text-[13.5px] leading-relaxed font-light">{vp.body}</p>
                  </div>
                </motion.li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-3 mt-10 items-stretch sm:items-center justify-center lg:justify-start">
              <a href={MT5_DOWNLOADS.webTerminal} target="_blank" rel="noopener noreferrer" className="btn-solid-brand !h-12 !px-7" data-testid="mt5-cta-web">
                {t("homepage.mt5.ctaWeb", "Launch MT5 Web")} <ArrowRight size={15} />
              </a>
              <a href="/platforms/mt5" className="btn-ghost-rotating text-sm px-7" data-testid="mt5-cta-overview">
                {t("homepage.mt5.ctaOverview", "Platform overview")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
